import React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  AlertIndicator,
  alertTypes,
  DateInput,
  getTodayIsoDateString,
  ModalButtonsContainer,
  REQUIRED_ERROR_MESSAGE,
  showErrorDialog,
  showToast,
  TextButton,
} from "@SeemplicityDev/shared";
import GenericField, {
  fieldTypeOptions,
} from "../../../Ticketing/GenericField/GenericField";
import { useCreateCreditPack } from "../../../AnalystRuns/AnalystRunsApi";
import type {
  CreateCreditPackRequest,
  CreditPack,
} from "../../../AnalystRuns/types";
import useLogger from "../../../Logger/useLogger";
import {
  BATCH_TYPE_OPTIONS,
  CREDIT_PACK_CREATED_MESSAGE,
  CREDIT_PACK_FORM_DESCRIPTION,
  CREDIT_PACK_SUBMIT_ERROR_MESSAGE,
  CREDITS_MUST_BE_POSITIVE_MESSAGE,
  EXPIRY_AFTER_PURCHASE_MESSAGE,
  EXPIRY_NOT_PAST_MESSAGE,
} from "./constants";

interface CreditPackFormData {
  credits_purchased: string;
  purchase_date: string;
  expiry_date: string;
  batch_type: string;
  notes: string;
}

interface CreditPackFormProps {
  accountUuid: string;
  pack?: CreditPack;
  onCancel: () => void;
  onSuccess?: () => void;
}

const DEFAULT_VALUES: CreditPackFormData = {
  credits_purchased: "",
  purchase_date: "",
  expiry_date: "",
  batch_type: "",
  notes: "",
};

const toFormValues = (pack: CreditPack): CreditPackFormData => ({
  credits_purchased: String(pack.credits_purchased),
  purchase_date: (pack.start_date ?? "").slice(0, 10),
  expiry_date: (pack.expiry_date ?? "").slice(0, 10),
  batch_type: pack.batch_type ?? "",
  notes: pack.notes ?? "",
});

const readInputValue = (inputVal: unknown): string => {
  if (inputVal && typeof inputVal === "object" && "target" in inputVal) {
    return String(
      (inputVal as React.ChangeEvent<HTMLInputElement>).target.value ?? ""
    );
  }
  return String(inputVal ?? "");
};

const backendErrorMessage = (err: unknown): string => {
  const detail = (
    err as { response?: { data?: { detail?: unknown } } } | undefined
  )?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const joined = detail
      .map((d) => (d as { msg?: string })?.msg)
      .filter(Boolean)
      .join("; ");
    if (joined) {
      return joined;
    }
  }
  return CREDIT_PACK_SUBMIT_ERROR_MESSAGE;
};

const CreditPackForm: React.FC<CreditPackFormProps> = ({
  accountUuid,
  pack,
  onCancel,
  onSuccess,
}) => {
  const isView = !!pack;
  const { logError } = useLogger();
  const createCreditPack = useCreateCreditPack(accountUuid);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors, isValid, isSubmitting },
  } = useForm<CreditPackFormData>({
    mode: "all",
    defaultValues: pack ? toFormValues(pack) : DEFAULT_VALUES,
  });

  const isRequestInFlight = isSubmitting || createCreditPack.isPending;

  const validateExpiryDate = (expiryIso: string): string | true => {
    if (!expiryIso) {
      return true;
    }
    if (expiryIso < getTodayIsoDateString()) {
      return EXPIRY_NOT_PAST_MESSAGE;
    }
    const purchaseIso = getValues("purchase_date");
    if (purchaseIso && expiryIso <= purchaseIso) {
      return EXPIRY_AFTER_PURCHASE_MESSAGE;
    }
    return true;
  };

  const onSubmit = (formData: CreditPackFormData) => {
    const body: CreateCreditPackRequest = {
      credits_purchased: Number(formData.credits_purchased),
      purchase_date: formData.purchase_date || undefined,
      expiry_date: formData.expiry_date || undefined,
      batch_type: formData.batch_type || null,
      notes: formData.notes.trim() || null,
    };
    return createCreditPack
      .mutateAsync(body)
      .then(() => {
        showToast({ message: CREDIT_PACK_CREATED_MESSAGE });
        onSuccess?.();
      })
      .catch((err: unknown) => {
        logError(err, "Failed to create credit pack");
        showErrorDialog({ message: backendErrorMessage(err) });
      });
  };

  return (
    <form className="account-configs-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="modal-scroll-area">
        {!isView && (
          <AlertIndicator
            type={alertTypes.INFO}
            content={CREDIT_PACK_FORM_DESCRIPTION}
            wrapperClassName="account-configs-form-description"
            hasMaxWidth={false}
          />
        )}
        <Controller
          name="credits_purchased"
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
            validate: (value: string) =>
              Number(value) > 0 || CREDITS_MUST_BE_POSITIVE_MESSAGE,
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <GenericField
              input={fieldTypeOptions.NUMBER}
              name="credits_purchased"
              label="Credits Purchased"
              description="Number of credits in the batch (must be greater than 0)."
              placeholder="Number of credits"
              required={!isView}
              hideLabelHelper
              disabled={isView}
              value={value}
              onBlur={onBlur}
              onChange={(inputVal: unknown) =>
                onChange(readInputValue(inputVal))
              }
              error={errors.credits_purchased ?? null}
            />
          )}
        />

        <div className="account-configs-form-row">
          <Controller
            name="purchase_date"
            control={control}
            rules={{ deps: ["expiry_date"] }}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateInput
                name="purchase_date"
                label="Purchase Date"
                labelTop
                hideLabelHelper
                readOnly={isView}
                value={value || null}
                onBlur={onBlur}
                onChange={(e: { target: { value: string } }) =>
                  onChange(e.target.value)
                }
              />
            )}
          />

          <Controller
            name="expiry_date"
            control={control}
            rules={{ validate: validateExpiryDate }}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateInput
                name="expiry_date"
                label="Expiry Date"
                labelTop
                hideLabelHelper
                readOnly={isView}
                value={value || null}
                onBlur={onBlur}
                onChange={(e: { target: { value: string } }) =>
                  onChange(e.target.value)
                }
                error={errors.expiry_date?.message ?? null}
              />
            )}
          />
        </div>

        <Controller
          name="batch_type"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <GenericField
              input={isView ? fieldTypeOptions.TEXT : fieldTypeOptions.SELECT}
              name="batch_type"
              label="Batch Type"
              description="Type of the purchased batch. Defaults to 'Starter pack'."
              placeholder="Select type (optional)"
              hideLabelHelper
              disabled={isView}
              clearable={!isView}
              options={isView ? undefined : BATCH_TYPE_OPTIONS}
              value={value}
              onBlur={onBlur}
              onChange={(inputVal: unknown) =>
                onChange((inputVal as string | undefined) ?? "")
              }
            />
          )}
        />

        <Controller
          name="notes"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <GenericField
              input={fieldTypeOptions.TEXT}
              name="notes"
              label="Notes"
              description="Internal note (order ID, reimbursement context, etc.)."
              placeholder="Internal notes (optional)"
              hideLabelHelper
              disabled={isView}
              value={value}
              onBlur={onBlur}
              onChange={(inputVal: unknown) =>
                onChange(readInputValue(inputVal))
              }
            />
          )}
        />
      </div>

      <ModalButtonsContainer spaceBetween>
        <TextButton
          type="button"
          data-testid="credit-packs-back"
          label="< Back to list"
          disabled={isRequestInFlight}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onCancel();
          }}
        />
        {!isView && (
          <TextButton
            type="submit"
            data-testid="credit-packs-form-submit"
            label="Create"
            buttonStyle="primary"
            isSubmitting={isRequestInFlight}
            rightIcon={isRequestInFlight ? "seem-icon-spinner spinner" : ""}
            disabled={!isValid || isRequestInFlight}
          />
        )}
      </ModalButtonsContainer>
    </form>
  );
};

export default CreditPackForm;
